import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { metaPixel } from '@/utils/meta-pixel';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Construire l'email préformaté
    const emailSubject = encodeURIComponent(formData.subject || 'Contact depuis DPEWizard');
    const emailBody = encodeURIComponent(
      `Bonjour,\n\n${formData.message}\n\nCordialement,\n${formData.name}\n${formData.email}`
    );
    
    // Ouvrir le client email par défaut
    window.location.href = `mailto:support@dpewizard.com?subject=${emailSubject}&body=${emailBody}`;
    
    // Track contact with Meta Pixel
    metaPixel.trackContact('email_form');
    
    // Afficher le message de succès
    setSuccess(true);
    
    // Réinitialiser le formulaire après un délai
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setSuccess(false);
    }, 3000);
  };

  return (
    <>
      <Helmet>
        <title>Contact - DPEWizard</title>
        <meta name="description" content="Contactez l'équipe DPEWizard pour toute question concernant nos services d'analyse DPE." />
        <link rel="canonical" href="https://www.dpewizard.com/contact" />
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Message d'accueil */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Contactez-nous
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Une question sur nos analyses DPE ? Notre équipe est à votre écoute et vous répond rapidement.
          </p>
        </div>

        {/* Formulaire */}
        <Card>
          <CardContent className="p-8">
            {success ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Email ouvert !
                </h3>
                <p className="text-gray-600 mb-6">
                  Votre client email s'est ouvert avec le message préformaté. 
                  Il ne vous reste plus qu'à cliquer sur "Envoyer".
                </p>
                <Button
                  variant="outline"
                  onClick={() => setSuccess(false)}
                >
                  Envoyer un autre message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    id="name"
                    name="name"
                    label="Votre nom"
                    placeholder="Jean Dupont"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    label="Votre email"
                    placeholder="jean.dupont@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <Input
                  id="subject"
                  name="subject"
                  label="Sujet"
                  placeholder="Question sur l'analyse DPE"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Votre message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Décrivez votre question ou votre demande..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  size="lg"
                  icon={<Send className="w-4 h-4 mr-2" />}
                >
                  Ouvrir mon client email
                </Button>
                
                <p className="text-center text-sm text-gray-500 mt-4">
                  En cliquant, votre client email s'ouvrira avec le message préformaté
                </p>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Note simple */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Notre équipe vous répondra dans les plus brefs délais à{' '}
            <a href="mailto:support@dpewizard.com" className="text-blue-600 hover:text-blue-700 font-medium">
              support@dpewizard.com
            </a>
          </p>
        </div>
      </div>
    </>
  );
};

export default ContactPage;